import type { WeaponSkinDefinition } from './weapon-skin-types';

/**
 * Starter weapon skin manifest.
 * ID ranges: ak47=1000+, m4a4=2000+, awp=4000+, deagle=5000+, glock=6000+,
 *            usp=7000+, p250=12000+, fiveseven=13000+, mp9=15000+,
 *            p90=17000+, nova=18000+, ssg08=19000+
 */
export const WEAPON_SKIN_MANIFEST: ReadonlyMap<number, WeaponSkinDefinition> = new Map([
  // ────────────────────────────────────────────────────────────────────────
  //  AK-47 skins (1000–1999)
  // ────────────────────────────────────────────────────────────────────────
  [1000, {
    id: 1000,
    name: 'Safari Mesh',
    weaponIds: ['ak47'],
    collection: 'Dust Collection',
    rarity: 'consumer',
    method: 'texture-swap',
    textures: {
      color: 'ak47/safari-mesh/color.ktx2',
      normal: 'ak47/safari-mesh/normal.ktx2',
      orm: 'ak47/safari-mesh/orm.ktx2',
    },
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/ak47/safari-mesh/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A basic mesh camo pattern. Functional, not flashy.',
  }],
  [1001, {
    id: 1001,
    name: 'Red Laminate',
    weaponIds: ['ak47'],
    collection: 'Arms Deal Collection',
    rarity: 'mil-spec',
    method: 'texture-swap',
    textures: {
      color: 'ak47/red-laminate/color.ktx2',
      normal: 'ak47/red-laminate/normal.ktx2',
      orm: 'ak47/red-laminate/orm.ktx2',
    },
    tint: 0xcc2222,
    roughness: 0.35,
    wearRange: [0.06, 0.50],
    thumbnailPath: '/textures/weapon-skins/ak47/red-laminate/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Red and black laminated wood furniture. A classic.',
  }],
  [1002, {
    id: 1002,
    name: 'Case Hardened',
    weaponIds: ['ak47'],
    collection: 'Arms Deal Collection',
    rarity: 'restricted',
    method: 'pattern-paint',
    textures: {
      color: 'ak47/case-hardened/color.ktx2',
      normal: 'ak47/case-hardened/normal.ktx2',
      orm: 'ak47/case-hardened/orm.ktx2',
    },
    pattern: {
      texturePath: 'ak47/case-hardened/pattern.ktx2',
      seedRange: [1, 999],
      colorPalette: [0x1a5276, 0x7d3c98, 0xf39c12, 0x2ecc71],
    },
    metalness: 0.85,
    roughness: 0.15,
    wearRange: [0.00, 0.70],
    thumbnailPath: '/textures/weapon-skins/ak47/case-hardened/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Heat-treated finish producing unique blue-gold patterns. Each one is different.',
  }],
  [1003, {
    id: 1003,
    name: 'Redline',
    weaponIds: ['ak47'],
    collection: 'Phoenix Collection',
    rarity: 'classified',
    method: 'texture-swap',
    textures: {
      color: 'ak47/redline/color.ktx2',
      normal: 'ak47/redline/normal.ktx2',
      orm: 'ak47/redline/orm.ktx2',
    },
    tint: 0xdd1111,
    roughness: 0.30,
    metalness: 0.10,
    wearRange: [0.10, 0.50],
    thumbnailPath: '/textures/weapon-skins/ak47/redline/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Matte black with red accent lines. Subtle and menacing.',
  }],
  [1004, {
    id: 1004,
    name: 'Neon Revolution',
    weaponIds: ['ak47'],
    collection: 'Gamma Collection',
    rarity: 'covert',
    method: 'custom-finish',
    textures: {
      color: 'ak47/neon-revolution/color.ktx2',
      normal: 'ak47/neon-revolution/normal.ktx2',
      orm: 'ak47/neon-revolution/orm.ktx2',
    },
    tint: 0x00ff88,
    roughness: 0.20,
    metalness: 0.15,
    emissiveColor: 0x00ff88,
    emissiveIntensity: 1.5,
    wearRange: [0.00, 0.65],
    thumbnailPath: '/textures/weapon-skins/ak47/neon-revolution/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Cyberpunk-inspired neon artwork. Glows faintly in the dark.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  M4A4 skins (2000–2999)
  // ────────────────────────────────────────────────────────────────────────
  [2000, {
    id: 2000,
    name: 'Urban DDPAT',
    weaponIds: ['m4a4'],
    collection: 'Dust Collection',
    rarity: 'consumer',
    method: 'texture-swap',
    textures: {
      color: 'm4a4/urban-ddpat/color.ktx2',
      normal: 'm4a4/urban-ddpat/normal.ktx2',
      orm: 'm4a4/urban-ddpat/orm.ktx2',
    },
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/m4a4/urban-ddpat/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Urban digital camouflage pattern.',
  }],
  [2001, {
    id: 2001,
    name: 'Dragon King',
    weaponIds: ['m4a4'],
    collection: 'Chroma Collection',
    rarity: 'restricted',
    method: 'texture-swap',
    textures: {
      color: 'm4a4/dragon-king/color.ktx2',
      normal: 'm4a4/dragon-king/normal.ktx2',
      orm: 'm4a4/dragon-king/orm.ktx2',
    },
    tint: 0xff6600,
    roughness: 0.25,
    metalness: 0.30,
    wearRange: [0.00, 0.55],
    thumbnailPath: '/textures/weapon-skins/m4a4/dragon-king/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A dragon motif in burnt orange and black.',
  }],
  [2002, {
    id: 2002,
    name: 'Asiimov',
    weaponIds: ['m4a4'],
    collection: 'Phoenix Collection',
    rarity: 'classified',
    method: 'texture-swap',
    textures: {
      color: 'm4a4/asiimov/color.ktx2',
      normal: 'm4a4/asiimov/normal.ktx2',
      orm: 'm4a4/asiimov/orm.ktx2',
    },
    tint: 0xff8800,
    roughness: 0.20,
    metalness: 0.05,
    wearRange: [0.18, 1.00],
    thumbnailPath: '/textures/weapon-skins/m4a4/asiimov/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'White and orange sci-fi design. High-contrast and iconic.',
  }],
  [2003, {
    id: 2003,
    name: 'Howl',
    weaponIds: ['m4a4'],
    collection: 'Howl Collection',
    rarity: 'covert',
    method: 'texture-swap',
    textures: {
      color: 'm4a4/howl/color.ktx2',
      normal: 'm4a4/howl/normal.ktx2',
      orm: 'm4a4/howl/orm.ktx2',
    },
    tint: 0xff3300,
    roughness: 0.22,
    metalness: 0.08,
    wearRange: [0.00, 0.40],
    thumbnailPath: '/textures/weapon-skins/m4a4/howl/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A snarling wolf engulfed in flames. The rarest M4A4.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  AWP skins (4000–4999)
  // ────────────────────────────────────────────────────────────────────────
  [4000, {
    id: 4000,
    name: 'Safari Mesh',
    weaponIds: ['awp'],
    collection: 'Dust Collection',
    rarity: 'consumer',
    method: 'texture-swap',
    textures: {
      color: 'awp/safari-mesh/color.ktx2',
      normal: 'awp/safari-mesh/normal.ktx2',
      orm: 'awp/safari-mesh/orm.ktx2',
    },
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/awp/safari-mesh/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Standard-issue mesh camouflage.',
  }],
  [4001, {
    id: 4001,
    name: 'Redline',
    weaponIds: ['awp'],
    collection: 'Phoenix Collection',
    rarity: 'classified',
    method: 'texture-swap',
    textures: {
      color: 'awp/redline/color.ktx2',
      normal: 'awp/redline/normal.ktx2',
      orm: 'awp/redline/orm.ktx2',
    },
    tint: 0xcc1111,
    roughness: 0.28,
    metalness: 0.12,
    wearRange: [0.10, 0.50],
    thumbnailPath: '/textures/weapon-skins/awp/redline/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Matte black with sharp red accents.',
  }],
  [4002, {
    id: 4002,
    name: 'Dragon Lore',
    weaponIds: ['awp'],
    collection: 'Cobblestone Collection',
    rarity: 'covert',
    method: 'custom-finish',
    textures: {
      color: 'awp/dragon-lore/color.ktx2',
      normal: 'awp/dragon-lore/normal.ktx2',
      orm: 'awp/dragon-lore/orm.ktx2',
    },
    tint: 0xd4a017,
    roughness: 0.18,
    metalness: 0.40,
    emissiveColor: 0xd4a017,
    emissiveIntensity: 0.8,
    wearRange: [0.00, 0.50],
    thumbnailPath: '/textures/weapon-skins/awp/dragon-lore/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A gold and emerald dragon. The most coveted sniper skin.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  Desert Eagle skins (5000–5999)
  // ────────────────────────────────────────────────────────────────────────
  [5000, {
    id: 5000,
    name: 'Urban DDPAT',
    weaponIds: ['deagle'],
    collection: 'Dust Collection',
    rarity: 'consumer',
    method: 'texture-swap',
    textures: {
      color: 'deagle/urban-ddpat/color.ktx2',
      normal: 'deagle/urban-ddpat/normal.ktx2',
      orm: 'deagle/urban-ddpat/orm.ktx2',
    },
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/deagle/urban-ddpat/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Urban digital camouflage on a hand cannon.',
  }],
  [5001, {
    id: 5001,
    name: 'Blaze',
    weaponIds: ['deagle'],
    collection: 'Arms Deal Collection',
    rarity: 'classified',
    method: 'texture-swap',
    textures: {
      color: 'deagle/blaze/color.ktx2',
      normal: 'deagle/blaze/normal.ktx2',
      orm: 'deagle/blaze/orm.ktx2',
    },
    tint: 0xff4400,
    roughness: 0.15,
    metalness: 0.20,
    emissiveColor: 0xff6600,
    emissiveIntensity: 1.2,
    // L33: Intentionally narrow wear range — Blaze is a Factory New-only skin.
    // 0.00-0.08 means it can only drop as Factory New (< 0.07) or barely Minimal Wear.
    wearRange: [0.00, 0.08],
    thumbnailPath: '/textures/weapon-skins/deagle/blaze/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Engulfed in flames. The tightest wear range of any skin.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  Glock-18 skins (6000–6999)
  // ────────────────────────────────────────────────────────────────────────
  [6000, {
    id: 6000,
    name: 'Sand Dune',
    weaponIds: ['glock'],
    collection: 'Dust Collection',
    rarity: 'consumer',
    method: 'texture-swap',
    textures: {
      color: 'glock/sand-dune/color.ktx2',
      normal: 'glock/sand-dune/normal.ktx2',
      orm: 'glock/sand-dune/orm.ktx2',
    },
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/glock/sand-dune/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Desert sand finish. The most common Glock skin.',
  }],
  [6001, {
    id: 6001,
    name: 'Water Elemental',
    weaponIds: ['glock'],
    collection: 'Breakout Collection',
    rarity: 'restricted',
    method: 'texture-swap',
    textures: {
      color: 'glock/water-elemental/color.ktx2',
      normal: 'glock/water-elemental/normal.ktx2',
      orm: 'glock/water-elemental/orm.ktx2',
    },
    tint: 0x2288cc,
    roughness: 0.25,
    metalness: 0.10,
    wearRange: [0.00, 0.65],
    thumbnailPath: '/textures/weapon-skins/glock/water-elemental/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A water spirit design in deep blue tones.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  USP-S skins (7000–7999)
  // ────────────────────────────────────────────────────────────────────────
  [7000, {
    id: 7000,
    name: 'Kill Confirmed',
    weaponIds: ['usp'],
    collection: 'Shadow Collection',
    rarity: 'covert',
    method: 'texture-swap',
    textures: {
      color: 'usp/kill-confirmed/color.ktx2',
      normal: 'usp/kill-confirmed/normal.ktx2',
      orm: 'usp/kill-confirmed/orm.ktx2',
    },
    tint: 0xcc2222,
    roughness: 0.22,
    metalness: 0.15,
    wearRange: [0.00, 0.65],
    thumbnailPath: '/textures/weapon-skins/usp/kill-confirmed/thumb.webp',
    team: 'CT',
    statTrakEligible: true,
    description: 'A comic-book skull design. Bold and unmistakable.',
  }],
  [7001, {
    id: 7001,
    name: 'Neo-Noir',
    weaponIds: ['usp'],
    collection: 'Prisma Collection',
    rarity: 'classified',
    method: 'texture-swap',
    textures: {
      color: 'usp/neo-noir/color.ktx2',
      normal: 'usp/neo-noir/normal.ktx2',
      orm: 'usp/neo-noir/orm.ktx2',
    },
    tint: 0x442266,
    roughness: 0.28,
    metalness: 0.10,
    wearRange: [0.00, 0.60],
    thumbnailPath: '/textures/weapon-skins/usp/neo-noir/thumb.webp',
    team: 'CT',
    statTrakEligible: true,
    description: 'A femme fatale noir illustration in deep purple tones.',
  }],
  [7002, {
    id: 7002,
    name: 'Cortex',
    weaponIds: ['usp'],
    collection: 'Prisma Collection',
    rarity: 'restricted',
    method: 'texture-swap',
    textures: {
      color: 'usp/cortex/color.ktx2',
      normal: 'usp/cortex/normal.ktx2',
      orm: 'usp/cortex/orm.ktx2',
    },
    tint: 0xffcc00,
    roughness: 0.30,
    metalness: 0.08,
    wearRange: [0.00, 0.55],
    thumbnailPath: '/textures/weapon-skins/usp/cortex/thumb.webp',
    team: 'CT',
    statTrakEligible: true,
    description: 'Cartoon brain art in bright yellow and pink.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  P250 skins (12000–12999)
  // ────────────────────────────────────────────────────────────────────────
  [12000, {
    id: 12000,
    name: 'Mehndi',
    weaponIds: ['p250'],
    collection: 'Bravo Collection',
    rarity: 'classified',
    method: 'texture-swap',
    textures: {
      color: 'p250/mehndi/color.ktx2',
      normal: 'p250/mehndi/normal.ktx2',
      orm: 'p250/mehndi/orm.ktx2',
    },
    tint: 0x228844,
    roughness: 0.35,
    metalness: 0.12,
    wearRange: [0.00, 0.60],
    thumbnailPath: '/textures/weapon-skins/p250/mehndi/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Intricate henna-style patterns on deep green.',
  }],
  [12001, {
    id: 12001,
    name: 'See Ya Later',
    weaponIds: ['p250'],
    collection: 'Prisma 2 Collection',
    rarity: 'restricted',
    method: 'texture-swap',
    textures: {
      color: 'p250/see-ya-later/color.ktx2',
      normal: 'p250/see-ya-later/normal.ktx2',
      orm: 'p250/see-ya-later/orm.ktx2',
    },
    tint: 0x00ccff,
    roughness: 0.25,
    metalness: 0.10,
    wearRange: [0.00, 0.55],
    thumbnailPath: '/textures/weapon-skins/p250/see-ya-later/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A retro alligator illustration in teal and turquoise.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  Five-SeveN skins (13000–13999)
  // ────────────────────────────────────────────────────────────────────────
  [13000, {
    id: 13000,
    name: 'Monkey Business',
    weaponIds: ['fiveseven'],
    collection: 'Falchion Collection',
    rarity: 'restricted',
    method: 'texture-swap',
    textures: {
      color: 'fiveseven/monkey-business/color.ktx2',
      normal: 'fiveseven/monkey-business/normal.ktx2',
      orm: 'fiveseven/monkey-business/orm.ktx2',
    },
    tint: 0x44bb22,
    roughness: 0.40,
    metalness: 0.05,
    wearRange: [0.00, 0.65],
    thumbnailPath: '/textures/weapon-skins/fiveseven/monkey-business/thumb.webp',
    team: 'CT',
    statTrakEligible: true,
    description: 'A playful banana pattern. Silly but stylish.',
  }],
  [13001, {
    id: 13001,
    name: 'Hyper Beast',
    weaponIds: ['fiveseven'],
    collection: 'Chroma 2 Collection',
    rarity: 'covert',
    method: 'texture-swap',
    textures: {
      color: 'fiveseven/hyper-beast/color.ktx2',
      normal: 'fiveseven/hyper-beast/normal.ktx2',
      orm: 'fiveseven/hyper-beast/orm.ktx2',
    },
    tint: 0xff44aa,
    roughness: 0.20,
    metalness: 0.18,
    wearRange: [0.00, 0.50],
    thumbnailPath: '/textures/weapon-skins/fiveseven/hyper-beast/thumb.webp',
    team: 'CT',
    statTrakEligible: true,
    description: 'A vivid monster illustration in neon pink and blue.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  MP9 skins (15000–15999)
  // ────────────────────────────────────────────────────────────────────────
  [15000, {
    id: 15000,
    name: 'Hydra',
    weaponIds: ['mp9'],
    collection: 'Hydra Collection',
    rarity: 'mil-spec',
    method: 'texture-swap',
    textures: {
      color: 'mp9/hydra/color.ktx2',
      normal: 'mp9/hydra/normal.ktx2',
      orm: 'mp9/hydra/orm.ktx2',
    },
    tint: 0x226622,
    roughness: 0.35,
    metalness: 0.10,
    wearRange: [0.00, 0.65],
    thumbnailPath: '/textures/weapon-skins/mp9/hydra/thumb.webp',
    team: 'CT',
    statTrakEligible: true,
    description: 'Dark green with a multi-headed serpent motif.',
  }],
  [15001, {
    id: 15001,
    name: 'Rose Iron',
    weaponIds: ['mp9'],
    collection: 'Spectrum Collection',
    rarity: 'industrial',
    method: 'texture-swap',
    textures: {
      color: 'mp9/rose-iron/color.ktx2',
      normal: 'mp9/rose-iron/normal.ktx2',
      orm: 'mp9/rose-iron/orm.ktx2',
    },
    tint: 0xcc6688,
    roughness: 0.40,
    metalness: 0.25,
    wearRange: [0.06, 0.75],
    thumbnailPath: '/textures/weapon-skins/mp9/rose-iron/thumb.webp',
    team: 'CT',
    statTrakEligible: true,
    description: 'Rose-tinted iron oxide finish with floral engravings.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  P90 skins (17000–17999)
  // ────────────────────────────────────────────────────────────────────────
  [17000, {
    id: 17000,
    name: 'Asiimov',
    weaponIds: ['p90'],
    collection: 'Phoenix Collection',
    rarity: 'classified',
    method: 'texture-swap',
    textures: {
      color: 'p90/asiimov/color.ktx2',
      normal: 'p90/asiimov/normal.ktx2',
      orm: 'p90/asiimov/orm.ktx2',
    },
    tint: 0xff8800,
    roughness: 0.20,
    metalness: 0.05,
    wearRange: [0.00, 0.90],
    thumbnailPath: '/textures/weapon-skins/p90/asiimov/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'White and orange sci-fi panels. A futuristic look.',
  }],
  [17001, {
    id: 17001,
    name: 'Death by Kitty',
    weaponIds: ['p90'],
    collection: 'Bravo Collection',
    rarity: 'restricted',
    method: 'texture-swap',
    textures: {
      color: 'p90/death-by-kitty/color.ktx2',
      normal: 'p90/death-by-kitty/normal.ktx2',
      orm: 'p90/death-by-kitty/orm.ktx2',
    },
    tint: 0xff66aa,
    roughness: 0.30,
    metalness: 0.08,
    wearRange: [0.00, 0.65],
    thumbnailPath: '/textures/weapon-skins/p90/death-by-kitty/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Cute yet deadly cat illustration in pink and black.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  Nova skins (18000–18999)
  // ────────────────────────────────────────────────────────────────────────
  [18000, {
    id: 18000,
    name: 'Hyper Beast',
    weaponIds: ['nova'],
    collection: 'Chroma 2 Collection',
    rarity: 'restricted',
    method: 'texture-swap',
    textures: {
      color: 'nova/hyper-beast/color.ktx2',
      normal: 'nova/hyper-beast/normal.ktx2',
      orm: 'nova/hyper-beast/orm.ktx2',
    },
    tint: 0x44aaff,
    roughness: 0.25,
    metalness: 0.15,
    wearRange: [0.00, 0.65],
    thumbnailPath: '/textures/weapon-skins/nova/hyper-beast/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A colorful monster illustration with neon highlights.',
  }],

  // ────────────────────────────────────────────────────────────────────────
  //  SSG 08 skins (19000–19999)
  // ────────────────────────────────────────────────────────────────────────
  [19000, {
    id: 19000,
    name: 'Blood in the Water',
    weaponIds: ['ssg08'],
    collection: 'Huntsman Collection',
    rarity: 'covert',
    method: 'texture-swap',
    textures: {
      color: 'ssg08/blood-in-the-water/color.ktx2',
      normal: 'ssg08/blood-in-the-water/normal.ktx2',
      orm: 'ssg08/blood-in-the-water/orm.ktx2',
    },
    tint: 0x2255cc,
    roughness: 0.25,
    metalness: 0.12,
    wearRange: [0.06, 0.35],
    thumbnailPath: '/textures/weapon-skins/ssg08/blood-in-the-water/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A great white shark tearing through ocean waves.',
  }],
  [19001, {
    id: 19001,
    name: 'Dragonfire',
    weaponIds: ['ssg08'],
    collection: 'Gamma 2 Collection',
    rarity: 'classified',
    method: 'texture-swap',
    textures: {
      color: 'ssg08/dragonfire/color.ktx2',
      normal: 'ssg08/dragonfire/normal.ktx2',
      orm: 'ssg08/dragonfire/orm.ktx2',
    },
    tint: 0xff4400,
    roughness: 0.22,
    metalness: 0.18,
    emissiveColor: 0xff4400,
    emissiveIntensity: 0.6,
    wearRange: [0.00, 0.55],
    thumbnailPath: '/textures/weapon-skins/ssg08/dragonfire/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A fire-breathing dragon wrapping the barrel. Faintly glows.',
  }],
]);
